import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { NavLinkItem, useSectionNav } from "./NavLinkItem";
import { NAV_LINKS } from "@/data/content";
import { EASE } from "./Reveal";

const MobileMenu = ({ onClose }) => (
  <motion.div
    className="fixed inset-0 z-[60] bg-[#16302B] grain flex flex-col px-6 pt-6 pb-10 lg:hidden"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.35 }}
    data-testid="mobile-menu"
  >
    <div className="flex items-center justify-between">
      <Logo variant="light" size={36} />
      <button onClick={onClose} className="p-2 text-white" aria-label="Close menu" data-testid="mobile-menu-close-button">
        <X className="h-6 w-6" strokeWidth={1.25} />
      </button>
    </div>
    <nav className="mt-16 flex flex-col gap-5">
      {NAV_LINKS.map((l, i) => (
        <motion.div key={l.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 + i * 0.05, duration: 0.6, ease: EASE }}>
          <NavLinkItem link={l} onDone={onClose} className="font-display text-4xl text-white" />
        </motion.div>
      ))}
    </nav>
  </motion.div>
);

export const Nav = () => {
  const { pathname } = useLocation();
  const go = useSectionNav();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const solid = scrolled || pathname !== "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
          solid ? "bg-[#F6F2E8]/90 backdrop-blur-xl border-[#D8CFBC]" : "bg-transparent border-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between gap-6">
          <Link to="/" data-testid="nav-logo-link" className="shrink-0">
            <Logo variant={solid ? "dark" : "light"} size={38} />
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <NavLinkItem
                key={l.id}
                link={l}
                className={`link-underline text-[13px] tracking-wide transition-colors duration-300 ${solid ? "text-[#221F1B] hover:text-[#16302B]" : "text-white/85 hover:text-white"}`}
              />
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={() => go("contact")}
              className={`btn-brass ${solid ? "" : "on-dark"} px-4 sm:px-5 py-2.5 text-[12px] sm:text-[13px] font-medium tracking-wide`}
              data-testid="nav-book-discovery-button"
            >
              <span className="hidden sm:inline">Book a Discovery Session</span>
              <span className="sm:hidden">Book a Session</span>
            </button>
            <button
              onClick={() => setOpen(true)}
              className={`lg:hidden p-2 ${solid ? "text-[#16302B]" : "text-white"}`}
              aria-label="Open menu"
              data-testid="mobile-menu-open-button"
            >
              <Menu className="h-6 w-6" strokeWidth={1.25} />
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
